import type { ReactNode } from 'react';
import {
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
} from 'react-aria-components';
import { cx } from '@/utils/cx';

export type RadioCardOption<T extends string> = {
  value: T;
  label: string;
  description?: string;
};

type RadioCardGroupProps<T extends string> = {
  label: string;
  value: T | undefined;
  onChange: (value: T) => void;
  options: ReadonlyArray<RadioCardOption<T>>;
  columns?: 1 | 2 | 3;
  errorMessage?: string;
  describedBy?: string;
};

const COLUMNS_CLASS: Record<1 | 2 | 3, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
};

export const RadioCardGroup = <T extends string>({
  label,
  value,
  onChange,
  options,
  columns = 2,
  errorMessage,
  describedBy,
}: RadioCardGroupProps<T>) => {
  const isInvalid = !!errorMessage;

  return (
    <AriaRadioGroup
      aria-label={label}
      aria-describedby={describedBy}
      value={value ?? ''}
      onChange={(next) => onChange(next as T)}
      isInvalid={isInvalid}
      className="flex flex-col gap-3"
    >
      <div className={cx('grid gap-3', COLUMNS_CLASS[columns])}>
        {options.map((opt) => (
          <RadioCard key={opt.value} option={opt} />
        ))}
      </div>
      {errorMessage ? (
        <p className="text-sm font-medium text-error-primary" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </AriaRadioGroup>
  );
};

type RadioCardChildren = {
  isSelected: boolean;
  isFocusVisible: boolean;
};

const RadioCard = <T extends string>({ option }: { option: RadioCardOption<T> }) => {
  const renderContent = ({ isSelected, isFocusVisible }: RadioCardChildren): ReactNode => (
    <span
      className={cx(
        'flex h-full min-h-[64px] cursor-pointer flex-col items-start justify-center rounded-2xl border bg-white px-5 py-4 text-left transition-all',
        isSelected
          ? 'border-black ring-1 ring-black'
          : 'border-gray-200 hover:border-gray-300',
        isFocusVisible && 'outline-2 outline-offset-2 outline-black',
      )}
    >
      <span className="text-[15px] font-semibold leading-tight text-gray-900">{option.label}</span>
      {option.description ? (
        <span
          className={cx(
            'mt-1 text-[13px] leading-snug',
            isSelected ? 'text-gray-700' : 'text-gray-500',
          )}
        >
          {option.description}
        </span>
      ) : null}
    </span>
  );

  return (
    <AriaRadio value={option.value} className="block focus:outline-hidden">
      {renderContent}
    </AriaRadio>
  );
};
